// @flow

import React, {useEffect, useState} from "react";
import {useHistory} from "react-router-dom";
import {useObserver} from "mobx-react-lite";
import {useFormik} from "formik";
import {Col, Form, Input, Row, Result, Button} from "antd";
import {LockOutlined} from "@ant-design/icons";
import {useTranslation} from "react-i18next";

import {useStore} from "../../hooks/useStore";
import {newPasswordFormValidation} from "./newPasswordValidation";
import {newPasswordFormType, newPasswordPropType} from "./newPasswordPropTypes";
import {CardContainer, PasswordButtonContainer} from "./newPasswordStyles";
import {CustomButton} from "../_defaultComponents/CustomButton/CustomButton";
import {LoginBackground} from "../login/loginBackground/LoginBackground";
import {LoginLayout} from "../layouts/loginLayout/LoginLayout";
import {LoginWrapper} from "../login/loginStyles";

const urlSearchParams = new URLSearchParams(window.location.search);
const params = Object.fromEntries(urlSearchParams.entries());
const hash = params.hash;

export const NewPasswordRedesign = ({token}: newPasswordPropType) => {
    const history = useHistory();
    const stores = useStore();
    const {t} = useTranslation(["newPassword"]);

    const [error, setError] = useState(false);

    useEffect(() => {
        if (hash) {
            getEmailAndHash();
        }
    }, [hash]);

    const getEmailAndHash = async () => {
        const result = await stores.meStore.getEmailAndHash(hash);
        if (result?.status !== 200) {
            if (result?.data?.message === "Wrong link") {
                setError(t("newPassword.validation.wrongLink", {ns: "newPassword"}));
            } else if (result?.data?.message === "Link expired") {
                setError(t("newPassword.validation.linkExpired", {ns: "newPassword"}));
            } else {
                setError(result.data.message);
            }
        } else {
            setError(false);
        }
        return;
    };

    const formik = useFormik({
        initialValues: {
            newPassword: "",
            passwordConfirm: ""
        },
        validationSchema: newPasswordFormValidation,
        onSubmit: async (values: newPasswordFormType) => {
            const result = await stores.meStore.resetPassword({
                newPassword: values.newPassword,
                hash
            });
            if (result) {
                history.push("/login");
            }
        }
    });
    const {values, errors, touched, handleChange, handleSubmit, isSubmitting, resetForm} = formik;

    return useObserver(() => (
        <LoginLayout>
            <LoginWrapper>
                {error ? (
                    <Result
                        status="404"
                        title={t("other.error", {ns: "common"})}
                        subTitle={error}
                        extra={
                            <Button type="primary" onClick={() => history.push("/")}>
                                {t("newPassword.actions.returnLogin", {ns: "newPassword"})}
                            </Button>
                        }
                    />
                ) : (
                    <>
                        <LoginBackground />
                        <CardContainer type="inner">
                            <h2
                                style={{
                                    fontWeight: "bold",
                                    fontSize: "28px",
                                    paddingTop: "50px",
                                    display: "flex",
                                    justifyContent: "center"
                                }}
                            >
                                Şifre Değiştir
                            </h2>
                            <Form
                                autoComplete="off"
                                onFinish={handleSubmit}
                                labelCol={{span: 24}}
                                wrapperCol={{span: 24}}
                                style={{
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}
                            >
                                <Col style={{width: "100%", padding: "30px"}}>
                                    <Form.Item
                                        label={t("fields.password", {ns: "common"})}
                                        validateStatus={
                                            errors.newPassword && touched.newPassword
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            errors.newPassword && touched.newPassword
                                                ? errors.newPassword
                                                : null
                                        }
                                    >
                                        <Input.Password
                                            name="newPassword"
                                            value={values.newPassword}
                                            onChange={handleChange}
                                            size="large"
                                            style={{
                                                borderRadius: "10px",
                                                width: "100%"
                                            }}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label={t("fields.passwordAgain", {ns: "common"})}
                                        validateStatus={
                                            errors.passwordConfirm && touched.passwordConfirm
                                                ? "error"
                                                : "success"
                                        }
                                        help={
                                            errors.passwordConfirm && touched.passwordConfirm
                                                ? errors.passwordConfirm
                                                : null
                                        }
                                    >
                                        <Input.Password
                                            name="passwordConfirm"
                                            value={values.passwordConfirm}
                                            onChange={handleChange}
                                            size="large"
                                            style={{
                                                borderRadius: "10px",
                                                width: "100%"
                                            }}
                                        />
                                    </Form.Item>
                                    <PasswordButtonContainer>
                                        <Row style={{width: "100%"}}>
                                            <CustomButton
                                                block
                                                disabled={isSubmitting}
                                                type="primary"
                                                htmlType="submit"
                                                style={{
                                                    backgroundColor: "#aeff01",
                                                    borderRadius: "100px",
                                                    padding: "20px",
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    fontWeight: "bold",
                                                    border: "none",
                                                    color: "#000000"
                                                }}
                                            >
                                                {t("actions.save", {ns: "common"})}
                                            </CustomButton>
                                        </Row>
                                        <Row
                                            style={{
                                                borderBottom: "1px solid #000000",
                                                width: "100px"
                                            }}
                                        >
                                            <CustomButton
                                                block
                                                href="/login"
                                                type="ghost"
                                                onClick={event => {
                                                    event.preventDefault();
                                                    history.push("/login");
                                                }}
                                                style={{
                                                    backgroundColor: "transparent",
                                                    border: "none"
                                                }}
                                            >
                                                Girişe Dön
                                            </CustomButton>
                                        </Row>
                                    </PasswordButtonContainer>
                                </Col>
                            </Form>
                        </CardContainer>
                    </>
                )}
            </LoginWrapper>
        </LoginLayout>
    ));
};
